// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useCallback } from 'react';

import type { AssignedCustomProperty } from '@evolvemep/api-ts';
import { Popover, Stack, Flex, Text, Overlay } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import {
  CustomPropertiesList,
  type CustomPropertiesListProps,
} from 'components/Mantine/CustomProperties/CustomPropertiesList';
import { EditCustomPropertiesModal } from 'components/Mantine/CustomProperties/EditCustomPropertiesModal';
import { ManageCustomPropertiesButton } from 'components/Mantine/CustomProperties/ManageCustomPropertiesButton';
import type { BadgeSize } from 'components/Mantine/StatusBadge';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { BadgeButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/BadgeButton';

export const CustomPropertiesPopover = <T extends { customProperties: AssignedCustomProperty[] }>({
  size = 'xl',
  ...props
}: CustomPropertiesListProps<T> & {
  size?: BadgeSize;
}) => {
  const hasManageCustomPropertiesAccess = useHasAccess('customProperties', 'write');
  const [opened, setOpened] = useState(false);
  const [editingCustomProperty, setEditingCustomProperty] = useState(opened);
  const [managePropertiesOpened, setManagePropertiesOpened] = useState(false);

  const onStartEditingCustomProperty = useCallback(() => setEditingCustomProperty(true), []);
  const onEndEditingCustomProperty = useCallback(() => setEditingCustomProperty(false), []);

  return (
    <>
      <Popover
        opened={opened || editingCustomProperty}
        onChange={setOpened}
        closeOnClickOutside={!editingCustomProperty}
        position="bottom-end"
      >
        <Popover.Target>
          <AgGridStyleTooltip label="View and edit custom properties" hidden={opened} withArrow>
            <BadgeButton size={size} onClick={() => setOpened((o) => !o)}>
              <EvolveIcon icon="Manage" />
            </BadgeButton>
          </AgGridStyleTooltip>
        </Popover.Target>
        <Popover.Dropdown p={0} style={{ overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
          <Flex bg="gray.1" justify="space-between" align="center" px="md" mih={35} fz="sm" fw={600}>
            <Text fw={600}>{props.customPropertyTypeName} Custom Properties</Text>
            {hasManageCustomPropertiesAccess && (
              <ManageCustomPropertiesButton
                onClick={() => {
                  setManagePropertiesOpened(true);
                  setOpened(false);
                }}
              />
            )}
          </Flex>
          <Stack
            px="md"
            py="xs"
            miw={300}
            mah={400}
            gap={6}
            style={{
              overflowY: 'auto',
            }}
          >
            <CustomPropertiesList
              {...props}
              onStartEditingCustomProperty={onStartEditingCustomProperty}
              onEndEditingCustomProperty={onEndEditingCustomProperty}
            />
          </Stack>
          {editingCustomProperty && <Overlay opacity={0} />}
        </Popover.Dropdown>
      </Popover>

      <EditCustomPropertiesModal
        customPropertyTypeName={props.customPropertyTypeName}
        opened={managePropertiesOpened}
        onClose={() => {
          setManagePropertiesOpened(false);
          setOpened(true);
        }}
      />
    </>
  );
};
